import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, Copy, MessageSquareText, Link2, Loader2, CheckCircle2, RefreshCw } from "lucide-react";

export default function AdminTextToBuy() {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [cart, setCart] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/paylinks/recent").then((r) => setRecent(r.data.items || [])).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get(`/products?search=${encodeURIComponent(search.trim())}&limit=10&include_adult=true`)
        .then((r) => setResults(r.data.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product.id === p.id);
      if (ex) return c.map((x) => (x.product.id === p.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { product: p, qty: 1 }];
    });
  };
  const setQty = (id, qty) =>
    setCart((c) => (qty <= 0 ? c.filter((x) => x.product.id !== id) : c.map((x) => (x.product.id === id ? { ...x, qty } : x))));

  const subtotal = cart.reduce((s, x) => s + x.product.price * x.qty, 0);
  const total = subtotal * 1.06;

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  const submit = async () => {
    if (cart.length === 0) return toast.error("Add at least one product");
    if (!name.trim()) return toast.error("Customer name is required");
    if (sendNow && !phone && !email) return toast.error("Add a phone or email — or switch to 'Just create the link'");
    setSubmitting(true);
    try {
      const { data } = await api.post("/paylinks", {
        items: cart.map((x) => ({ product_id: x.product.id, quantity: x.qty })),
        customer_name: name.trim(),
        customer_phone: phone || null,
        customer_email: email || null,
        send: sendNow,
      });
      setCreated(data);
      setCart([]);
      loadRecent();
      toast.success(sendNow ? "Payment link sent" : "Payment link created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-text-to-buy">
      <h1 className="font-display font-black text-3xl tracking-tight">Text-to-Buy Links</h1>
      <p className="text-sm text-gray-600 mt-1">
        Pick products, generate a secure one-tap payment link, and text it to the customer. They tap, pay with their card, done — order lands in Orders automatically.
      </p>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6 mt-8 items-start">
        {/* LEFT — product picker */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input-base pl-9"
              placeholder="Search products by name, SKU or category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="ttb-search-input"
            />
          </div>
          <div className="mt-3 space-y-1.5">
            {searching && <div className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>}
            {!searching && search.trim() && results.length === 0 && <div className="text-xs text-gray-400">No matching products</div>}
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => add(p)}
                className="w-full flex items-center gap-3 p-2 border border-gray-200 hover:border-[#0A1C40] transition-colors text-left"
                data-testid={`ttb-result-${p.id}`}
              >
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt="" className="w-11 h-11 object-cover shrink-0" />
                ) : (
                  <div className="w-11 h-11 bg-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.sku} · {p.stock} in stock</div>
                </div>
                <div className="font-bold text-sm shrink-0">{fmtMoney(p.price)}</div>
                <Plus className="w-4 h-4 text-[#0A1C40] shrink-0" />
              </button>
            ))}
          </div>

          {/* Cart */}
          {cart.length > 0 && (
            <div className="mt-5 border-t border-gray-200 pt-4" data-testid="ttb-cart">
              <div className="ipv-eyebrow mb-2">In this link</div>
              {cart.map((x) => (
                <div key={x.product.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="flex-1 truncate font-medium">{x.product.name}</span>
                  <button onClick={() => setQty(x.product.id, x.qty - 1)} className="p-1 border border-gray-300" data-testid={`ttb-qty-minus-${x.product.id}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center font-bold">{x.qty}</span>
                  <button onClick={() => setQty(x.product.id, x.qty + 1)} className="p-1 border border-gray-300" data-testid={`ttb-qty-plus-${x.product.id}`}><Plus className="w-3 h-3" /></button>
                  <span className="w-20 text-right font-bold">{fmtMoney(x.product.price * x.qty)}</span>
                  <button onClick={() => setQty(x.product.id, 0)} className="text-gray-400 hover:text-red-600" data-testid={`ttb-remove-${x.product.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-dashed border-gray-300">
                <span>Subtotal</span><span className="font-bold">{fmtMoney(subtotal)}</span>
              </div>
              <div className="flex justify-between font-black">
                <span>Total (with 6% tax)</span><span data-testid="ttb-total">{fmtMoney(total)}</span>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — customer + send */}
        <div className="space-y-4">
          {created && (
            <div className="bg-green-50 border-2 border-green-600 p-5" data-testid="ttb-created">
              <div className="flex items-center gap-2 text-green-700 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5" /> Link ready — order {created.order_number} ({fmtMoney(created.total)})
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {created.sms_queued && "Texted to the customer. "}
                {created.email_queued && "Emailed. "}
                Expires in 48 hours.
              </div>
              <div className="flex gap-2 mt-3">
                <input readOnly className="input-base text-xs flex-1" value={created.url} data-testid="ttb-link-url" />
                <button onClick={() => copyUrl(created.url)} className="btn-primary px-3 shrink-0" data-testid="ttb-copy-link-btn">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <a href={`sms:${phone ? phone.replace(/[^+\d]/g, "") : ""}?&body=${encodeURIComponent(`Here's your secure IPVALUE payment link: ${created.url}`)}`}
                 className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#0A1C40] underline"
                 data-testid="ttb-open-messages">
                <MessageSquareText className="w-3.5 h-3.5" /> Open in Messages app
              </a>
            </div>
          )}

          <div className="bg-white border border-gray-200 p-5">
            <div className="ipv-eyebrow mb-3">Customer</div>
            <input className="input-base" placeholder="Customer name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="ttb-customer-name" />
            <input className="input-base mt-2" placeholder="Phone (for text)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ttb-customer-phone" />
            <input className="input-base mt-2" type="email" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ttb-customer-email" />

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setSendNow(true)}
                className={`p-3 border-2 text-left text-xs font-bold ${sendNow ? "border-[#0A1C40] bg-blue-50" : "border-gray-200"}`}
                data-testid="ttb-mode-send"
              >
                <MessageSquareText className="w-4 h-4 mb-1" /> Text / email it now
              </button>
              <button
                onClick={() => setSendNow(false)}
                className={`p-3 border-2 text-left text-xs font-bold ${!sendNow ? "border-[#0A1C40] bg-blue-50" : "border-gray-200"}`}
                data-testid="ttb-mode-copy"
              >
                <Link2 className="w-4 h-4 mb-1" /> Just create the link
              </button>
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="ttb-create-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {sendNow ? "Create & send payment link" : "Create payment link"}
            </button>
          </div>
        </div>
      </div>

      {/* RECENT */}
      <div className="mt-10 bg-white border border-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div className="ipv-eyebrow">Recent links</div>
          <button onClick={loadRecent} className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-[#0A1C40]" data-testid="ttb-refresh-recent">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Total</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Link</th>
              </tr>
            </thead>
            <tbody data-testid="ttb-recent-table">
              {recent.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-gray-400 text-xs">No payment links yet</td></tr>
              )}
              {recent.map((r) => (
                <tr key={r.order_number} className="border-b border-gray-100">
                  <td className="py-2 pr-3 font-mono font-bold">{r.order_number}</td>
                  <td className="py-2 pr-3">{r.customer_name}<div className="text-xs text-gray-400">{r.customer_phone || r.customer_email}</div></td>
                  <td className="py-2 pr-3 font-bold">{fmtMoney(r.total)}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full ${
                      r.status === "paid" ? "bg-green-100 text-green-700" : r.expired ? "bg-gray-100 text-gray-500" : "bg-amber-100 text-amber-700"
                    }`}>
                      {r.status === "paid" ? "PAID" : r.expired ? "EXPIRED" : "AWAITING"}
                    </span>
                  </td>
                  <td className="py-2">
                    <button onClick={() => copyUrl(r.url)} className="text-xs font-bold text-[#0A1C40] inline-flex items-center gap-1 hover:underline">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
